import { NxGridModule } from '@aposin/ng-aquila/grid';
import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Component, ChangeDetectionStrategy, Optional, ViewChild, Input, Output, NgModule } from '@angular/core';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import { clamp } from '@aposin/ng-aquila/utils';
import { coerceNumberProperty, coerceBooleanProperty } from '@angular/cdk/coercion';
import { LEFT_ARROW, DOWN_ARROW, RIGHT_ARROW, UP_ARROW } from '@angular/cdk/keycodes';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Decimal } from 'decimal.js';
import { fromEvent } from 'rxjs';
import * as i1 from '@angular/cdk/bidi';
import * as i2 from '@angular/cdk/a11y';

var EventType;
(function (EventType) {
    EventType[EventType["TOUCH"] = 0] = "TOUCH";
    EventType[EventType["MOUSE"] = 1] = "MOUSE";
})(EventType || (EventType = {}));
let nextId = 0;
const DEFAULT_MIN = 0;
const DEFAULT_MAX = 100;
const DEFAULT_STEP = 1;
class NxSliderComponent {
    constructor(elementRef, _changeDetectorRef, _ngZone, _dir, _focusMonitor) {
        this.elementRef = elementRef;
        this._changeDetectorRef = _changeDetectorRef;
        this._ngZone = _ngZone;
        this._dir = _dir;
        this._focusMonitor = _focusMonitor;
        this._id = `nx-slider-${nextId++}`;
        this._tabIndex = 0;
        this._min = DEFAULT_MIN;
        this._max = DEFAULT_MAX;
        this._disabled = false;
        this._inverted = false;
        this._thumbLabel = true;
        this._negative = false;
        this._hideLabels = false;
        /** An event is dispatched on each value change. */
        this.valueChange = new EventEmitter();
        this.isActive = false;
        this.dragSubscriptions = [];
        this.position = null;
        this._value = 0;
        this._step = DEFAULT_STEP;
        this._currentValue = 0;
        this._onChange = () => { };
        this._onTouched = () => { };
        /** Sets the customization function for the value which is displayed above the slider handle (Default:(value) => value). ). */
        this.valueFormatter = (value) => value;
        /** Sets the customization function for the label on the min-side of the slider (Default:(value) => value). */
        this.labelMinFormatter = (value) => value;
        /** Sets the customization function for the label on the max-side of the slider (Default:(value) => value). */
        this.labelMaxFormatter = (value) => value;
    }
    /** Sets the id of the slider. */
    set id(value) {
        if (this._id !== value) {
            this._id = value;
            this._changeDetectorRef.markForCheck();
        }
    }
    get id() {
        return this._id;
    }
    /** Sets the tabindex of the slider. */
    set tabindex(value) {
        this._tabIndex = coerceNumberProperty(value);
        this._changeDetectorRef.markForCheck();
    }
    get tabindex() {
        return this._disabled ? -1 : this._tabIndex;
    }
    /** Sets the minimum value (Default: 0). */
    set min(value) {
        this._min = coerceNumberProperty(value);
        this._changeDetectorRef.markForCheck();
    }
    get min() {
        return this._min;
    }
    /** Sets the maximum value (Default: 100). */
    set max(value) {
        this._max = coerceNumberProperty(value);
        this._changeDetectorRef.markForCheck();
    }
    get max() {
        return this._max;
    }
    /** Sets the step size by which the value of the slider can be increased or decreased (Default: 1). */
    get step() {
        return this._step;
    }
    set step(value) {
        this._step = coerceNumberProperty(value, this._step);
        if (this._step % 1 !== 0) {
            this._roundToDecimal = this._step.toString().split('.').pop().length;
        }
    }
    /** Sets the label which is displayed on top of the slider. */
    set label(value) {
        if (this._label !== value) {
            this._label = value;
            this._changeDetectorRef.markForCheck();
        }
    }
    get label() {
        return this._label;
    }
    /** Whether the input to the control of the slider should be disabled. */
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this._changeDetectorRef.markForCheck();
    }
    get disabled() {
        return this._disabled;
    }
    /** Whether the max value is to the right (false) or left (true).*/
    set inverted(value) {
        this._inverted = coerceBooleanProperty(value);
        this._changeDetectorRef.markForCheck();
    }
    get inverted() {
        return this._inverted;
    }
    /** Whether to display the thumb label on top of the slider.*/
    set thumbLabel(value) {
        this._thumbLabel = coerceBooleanProperty(value);
        this._changeDetectorRef.markForCheck();
    }
    get thumbLabel() {
        return this._thumbLabel;
    }
    /** Whether the negative set of styles is applied (Default: 'false').*/
    set negative(value) {
        this._negative = coerceBooleanProperty(value);
        this._changeDetectorRef.markForCheck();
    }
    get negative() {
        return this._negative;
    }
    /** Hides the min/max labels (Default: 'false'). */
    set hideLabels(value) {
        this._hideLabels = coerceBooleanProperty(value);
        this._changeDetectorRef.markForCheck();
    }
    get hideLabels() {
        return this._hideLabels;
    }
    ngAfterViewInit() {
        this._focusMonitor.monitor(this.handleElement);
    }
    /** Sets the current value of the slider. */
    set value(value) {
        this.writeValue(Number(value));
    }
    get value() {
        return this._value;
    }
    ngOnDestroy() {
        this.reset();
        this._focusMonitor.stopMonitoring(this.handleElement);
    }
    writeValue(value) {
        if (value !== this._value) {
            this._value = value;
            this.valueChange.emit(value);
            this._changeDetectorRef.markForCheck();
        }
    }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    /** @docs-private */
    isMinimum() {
        return this._value === this.min;
    }
    /** @docs-private
     * A valid step is either:
     * - the minimum because thats our anchor for all value evaluation and steps
     * - (_value - min) % step === 0
     */
    isValidStep() {
        const safeValue = new Decimal(this._value).minus(this.min);
        const modulo = safeValue.mod(this.step);
        return this.isMinimum() || modulo.cmp(0) === 0;
    }
    /** @docs-private
     * We have to look at two cases:
     * - current value is a valid multitude of the step size
     *   then we can safely add or subtract the step
     * - the value is not a valid multitude. this could be the max value or the value bound
     *   via nxValue
     *   then we look for the next closest value upwards or downwards
     *   decimal.js provides a nice utility function for this.
     */
    changeValue(valueDiff) {
        let newValue = new Decimal(this._value);
        if (this.isValidStep()) {
            newValue = newValue.plus(valueDiff);
        }
        else {
            // subtract the minimum to find the closest multitude then add the minimum again to get the valid slider step
            const minAdjustedValue = new Decimal(this._value).minus(this.min);
            newValue = valueDiff < 0 ? minAdjustedValue.toNearest(this.step, Decimal.ROUND_DOWN) : minAdjustedValue.toNearest(this.step, Decimal.ROUND_UP);
            newValue = newValue.plus(this.min);
        }
        // cast the Decimal object to a JS number before it gets returned
        let toNumber = newValue.toNumber();
        toNumber = clamp(toNumber, this.min, this.max);
        if (this.value !== toNumber) {
            this._onChange(toNumber);
            this.valueChange.emit(toNumber);
            this.value = toNumber;
        }
    }
    /** @docs-private */
    get percentageValue() {
        let percentageValue = (((this.value || 0) - this.min) / (this.max - this.min)) * 100;
        if (this.inverted) {
            percentageValue = 100 - percentageValue;
        }
        return clamp(percentageValue, 0, 100);
    }
    /** @docs-private */
    sliderClick(event) {
        if (this.disabled) {
            return;
        }
        this._focusHandleElement();
        event.stopPropagation();
        this.position = this.getPositionFromEvent(event);
        this.frameId = requestAnimationFrame(() => {
            this.valueByPosition();
            if (this.value !== this._currentValue) {
                this._currentValue = this.value;
                this._onChange(this.value);
            }
        });
    }
    /** @docs-private */
    focus() {
        if (this.disabled) {
            return;
        }
        this._focusHandleElement();
    }
    /** @docs-private */
    blur() {
        if (this.disabled) {
            return;
        }
    }
    /**
     * @docs-private
     * Prevent text selection when dragging the handle.
     */
    selectStart() {
        return false;
    }
    /** @docs-private */
    handleKeypress(event) {
        if (this.disabled) {
            return;
        }
        if (event.which < LEFT_ARROW || event.which > DOWN_ARROW) {
            return;
        }
        switch (event.which) {
            case this.inverted ? RIGHT_ARROW : LEFT_ARROW:
            case DOWN_ARROW:
                return this.changeValue(-this.step);
            case UP_ARROW:
            case this.inverted ? LEFT_ARROW : RIGHT_ARROW:
                return this.changeValue(this.step);
        }
    }
    /** @docs-private
     * this is called on mousedown or touchstart
     */
    dragStart(event) {
        if (this.disabled) {
            return;
        }
        this.isActive = true;
        const isTouchEvent = this.detectEventType(event) === EventType.TOUCH;
        this._currentValue = this.value;
        if (isTouchEvent) {
            this._ngZone.runOutsideAngular(() => {
                this.dragSubscriptions.push(fromEvent(document, 'touchmove').subscribe(this.handleDragMove.bind(this)));
                this.dragSubscriptions.push(fromEvent(document, 'touchend').subscribe(this.handleDragStop.bind(this)));
            });
            this._ngZone.run(() => {
                this.dragSubscriptions.push(fromEvent(document, 'touchcancel').subscribe(this.handleDragStop.bind(this)));
            });
        }
        else {
            this._ngZone.runOutsideAngular(() => {
                this.dragSubscriptions.push(fromEvent(document, 'mousemove').subscribe(this.handleDragMove.bind(this)));
            });
            this._ngZone.run(() => {
                this.dragSubscriptions.push(fromEvent(document, 'mouseup').subscribe(this.handleDragStop.bind(this)));
            });
        }
        this.position = this.getPositionFromEvent(event);
        this.runChangeObserver();
    }
    /** @docs-private */
    formatValue(value) {
        return this.valueFormatter(value);
    }
    /** @docs-private */
    formatLabelLeft() {
        return this.inverted ? this.formatLabelMax() : this.formatLabelMin();
    }
    /** @docs-private */
    formatLabelRight() {
        return this.inverted ? this.formatLabelMin() : this.formatLabelMax();
    }
    /** @docs-private */
    valueByPosition() {
        const isRTL = this._dir && this._dir.value === 'rtl';
        const rect = this.elementRef.nativeElement.getBoundingClientRect();
        const x = Math.max(rect.left, Math.min(rect.right, this.position.x));
        // position of slider relative to slider width
        let percent = (x - rect.left) / rect.width;
        if (this.inverted) {
            percent = 1 - percent;
        }
        if (isRTL) {
            percent = 1 - percent;
        }
        const exactValue = this.min + percent * (this.max - this.min);
        /**
         * edge case handling because of float precision errors
         * you couldn't reach the maximum
         */
        let closestValue;
        if (percent === 1) {
            closestValue = this.max;
        }
        else if (percent === 0) {
            closestValue = this.min;
        }
        else {
            closestValue = Math.round((exactValue - this.min) / this.step) * this.step + this.min;
        }
        if (this._roundToDecimal) {
            closestValue = this.roundToDecimal(closestValue);
        }
        closestValue = clamp(closestValue, this.min, this.max);
        this.value = closestValue;
    }
    /** @docs-private */
    roundToDecimal(value) {
        return parseFloat(value.toFixed(this._roundToDecimal));
    }
    /** @docs-private */
    formatLabelMin() {
        return this.labelMinFormatter(this.min);
    }
    /** @docs-private */
    formatLabelMax() {
        return this.labelMaxFormatter(this.max);
    }
    handleDragMove(event) {
        this.position = this.getPositionFromEvent(event);
    }
    handleDragStop(event) {
        this.reset();
        this.valueByPosition();
        if (this.value !== this._currentValue) {
            this._currentValue = this.value;
            this._onChange(this.value);
        }
    }
    runChangeObserver() {
        this.frameId = requestAnimationFrame(() => {
            this.valueByPosition();
            if (this.isActive) {
                this.runChangeObserver();
            }
        });
    }
    reset() {
        this.isActive = false;
        for (const subscription of this.dragSubscriptions) {
            subscription.unsubscribe();
        }
        this.dragSubscriptions = [];
        cancelAnimationFrame(this.frameId);
    }
    detectEventType(event) {
        return event.type.includes('touch') ? EventType.TOUCH : EventType.MOUSE;
    }
    getPositionFromEvent(event) {
        const eventType = this.detectEventType(event);
        const cursor = eventType === EventType.TOUCH ? event.touches.item(0) : event;
        return {
            x: cursor.clientX,
            y: cursor.clientY,
        };
    }
    /** @docs-private */
    _focusHandleElement() {
        this.handleElement.nativeElement.focus();
    }
}
NxSliderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: NxSliderComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: i1.Directionality, optional: true }, { token: i2.FocusMonitor }], target: i0.ɵɵFactoryTarget.Component });
NxSliderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: NxSliderComponent, selector: "nx-slider", inputs: { id: "id", tabindex: "tabindex", min: ["nxMin", "min"], max: ["nxMax", "max"], step: ["nxStep", "step"], label: ["nxLabel", "label"], disabled: "disabled", inverted: ["nxInverted", "inverted"], thumbLabel: "thumbLabel", negative: "negative", hideLabels: "hideLabels", valueFormatter: ["nxValueFormatter", "valueFormatter"], labelMinFormatter: ["nxLabelMinFormatter", "labelMinFormatter"], labelMaxFormatter: ["nxLabelMaxFormatter", "labelMaxFormatter"], value: ["nxValue", "value"] }, outputs: { valueChange: "nxValueChange" }, host: { listeners: { "keydown": "handleKeypress($event)" }, properties: { "attr.aria-disabled": "disabled ? true : null", "class.nx-slider--disabled": "disabled", "class.nx-slider--negative": "negative" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NxSliderComponent),
            multi: true,
        },
    ], viewQueries: [{ propertyName: "handleElement", first: true, predicate: ["handle"], descendants: true, static: true }], ngImport: i0, template: "<label class=\"nx-slider__label\" [id]=\"id + '-label'\" [attr.for]=\"id + '-handle'\" (click)=\"_focusHandleElement()\">{{ label }}</label>\n\n<div class=\"nx-slider__wrapper\" (click)=\"sliderClick($event)\">\n    <span class=\"nx-slider__background\">\n        <span class=\"nx-slider__filler\" [style.width.%]=\"percentageValue\"></span>\n\n        <div\n            class=\"nx-slider__handle\"\n            [id]=\"id + '-handle'\"\n            [attr.tabindex]=\"tabindex\"\n            [attr.aria-labelledby]=\"id + '-label'\"\n            role=\"slider\"\n            [attr.aria-valuemin]=\"formatLabelMin()\"\n            [attr.aria-valuemax]=\"formatLabelMax()\"\n            [attr.aria-valuetext]=\"formatValue(value)\"\n            [attr.aria-valuenow]=\"formatValue(value)\"\n            (mousedown)=\"dragStart($event)\"\n            (touchstart)=\"dragStart($event)\"\n            (selectstart)=\"selectStart()\"\n            #handle\n        >\n            <span *ngIf=\"thumbLabel\" class=\"nx-slider__value\"\n                >{{ formatValue(value) }}\n                <div class=\"nx-slider__arrow\"> </div>\n            </span>\n        </div>\n    </span>\n</div>\n\n<div *ngIf=\"!hideLabels\" class=\"nx-slider__label-container\">\n    <span class=\"nx-slider__value-label\">{{ formatLabelLeft() }}</span>\n    <span class=\"nx-slider__value-label\">{{ formatLabelRight() }}</span>\n</div>\n", styles: [":host{display:block}.nx-slider__label{font-size:var(--slider-label-font-size);line-height:var(--slider-label-line-height);font-weight:var(--slider-label-font-weight);letter-spacing:var(--slider-label-letter-spacing);color:var(--slider-label-color)}.nx-slider__wrapper{width:100%;padding:66px 0 18px}.nx-slider__background{position:relative;background-color:var(--slider-background-color);display:block;height:4px;border-radius:2px;white-space:nowrap;cursor:pointer}.nx-slider__filler{display:inline-block;height:4px;border-radius:2px;background-color:var(--slider-indicator-color);float:left}:host-context([dir=rtl]) .nx-slider__filler{float:right}.nx-slider__handle{display:inline-block;position:relative;width:24px;height:24px;border-radius:50%;box-shadow:var(--shadow-small);border-style:solid;border-width:1px;border-color:var(--slider-handle-border-color);background:var(--slider-handle-background-color);box-sizing:border-box;margin-left:-12px;top:-10px;z-index:2;cursor:-webkit-grab;cursor:grab}:host-context([dir=rtl]) .nx-slider__handle{margin-right:-12px;margin-left:0;margin-left:initial}.nx-slider__handle:focus{outline:none;border-color:var(--slider-handle-focus-border-color)}.nx-slider__handle:active{box-shadow:var(--shadow-small),inset 0 0 0 4px var(--slider-handle-background-color),inset 0 0 0 24px var(--slider-handle-active-inset-color);cursor:-webkit-grabbing;cursor:grabbing}.nx-slider__handle.cdk-keyboard-focused{border-color:var(--slider-handle-focus-border-color);box-shadow:var(--focus-box-shadow)}@media screen and (-ms-high-contrast: active){.nx-slider__handle.cdk-keyboard-focused{box-shadow:0 0 0 2px background,0 0 0 6px windowText;outline:4px solid CanvasText;outline-offset:2px}}.nx-slider__value{font-size:var(--slider-handle-value-font-size);line-height:var(--slider-handle-value-line-height);font-weight:var(--slider-handle-value-font-weight);letter-spacing:var(--slider-handle-value-letter-spacing);color:var(--slider-handle-value-color);border:1px solid;border-color:var(--slider-handle-border-color);border-radius:4px;box-shadow:var(--shadow-small);cursor:-webkit-grab;cursor:grab;height:24px;width:48px;text-align:center;right:8px;padding:3px 8px;position:relative;top:-40px;display:table;left:50%;transform:translate(-50%);background:var(--slider-value-background-color)}:host-context([dir=rtl]) .nx-slider__value{right:50%;left:8px;transform:translate(50%)}.nx-slider__value:active{cursor:-webkit-grabbing;cursor:grabbing}.nx-slider__arrow{width:6px;height:6px;border-right:1px solid;border-bottom:1px solid;left:50%;transform:translate(-50%,1px) rotate(45deg);position:absolute;box-shadow:-2px -2px 1px 1px var(--slider-value-background-color),2px 2px 4px var(--shadow-grey-color);background:var(--slider-value-background-color);border-color:var(--slider-handle-border-color)}.nx-slider__label-container{display:flex;width:100%;justify-content:space-between;font-size:var(--slider-value-label-font-size);line-height:var(--slider-value-label-line-height);font-weight:var(--slider-value-label-font-weight);letter-spacing:var(--slider-value-label-letter-spacing);color:var(--slider-value-label-color)}.nx-slider__max-value-label{text-align:right}:host(.nx-slider--disabled){cursor:not-allowed}:host(.nx-slider--disabled) .nx-slider__filler,:host(.nx-slider--disabled) .nx-slider__value,:host(.nx-slider--disabled) .nx-slider__value-label,:host(.nx-slider--disabled) .nx-slider__handle,:host(.nx-slider--disabled) .nx-slider__background{pointer-events:none;cursor:not-allowed}:host(.nx-slider--disabled) .nx-slider__value,:host(.nx-slider--disabled) .nx-slider__handle{background-color:var(--slider-value-disabled-background-color)}:host(.nx-slider--disabled) .nx-slider__filler{background-color:var(--slider-indicator-disabled-color)}:host(.nx-slider--disabled) .nx-slider__value{color:var(--slider-handle-value-disabled-color)}:host(.nx-slider--disabled) .nx-slider__label-container{color:var(--slider-value-label-disabled-color)}:host(.nx-slider--disabled) .nx-slider__arrow{background:var(--slider-value-disabled-background-color);box-shadow:-2px -2px 1px 1px var(--slider-value-disabled-background-color),2px 2px 4px #41414180}:host(.nx-slider--disabled) .nx-slider__background{background:var(--slider-disabled-background-color)}:host(.nx-slider--negative) .nx-slider__label,:host(.nx-slider--negative) .nx-slider__value-label{color:var(--negative)}:host(.nx-slider--negative) .nx-slider__filler{background-color:var(--negative)}:host(.nx-slider--negative) .nx-slider__background{background-color:var(--slider-negative-background-color)}:host(.nx-slider--negative.nx-slider--disabled) .nx-slider__label,:host(.nx-slider--negative.nx-slider--disabled) .nx-slider__value-label{color:var(--negative-01)}:host(.nx-slider--negative.nx-slider--disabled) .nx-slider__filler{background-color:var(--negative-01)}@media screen and (-ms-high-contrast: active){.nx-slider__background{background-color:buttonFace;border:1px solid buttonText}.nx-slider__filler{background-color:highlight}.nx-slider__handle{background-color:buttonFace;border-color:buttonText}.nx-slider__handle:hover,.nx-slider__handle:focus{background-color:highlight}.nx-slider__handle:active{background-color:buttonText}.nx-slider__value{border-color:buttonText;color:buttonText}:host(.nx-slider--disabled) .nx-slider__background{background-color:Canvas;border:1px solid GrayText}:host(.nx-slider--disabled) .nx-slider__filler{background-color:GrayText}:host(.nx-slider--disabled) .nx-slider__handle{background-color:GrayText}:host(.nx-slider--disabled) .nx-slider__value{border-color:GrayText;color:GrayText}:host(.nx-slider--disabled) .nx-slider__arrow{border-color:GrayText}:host.nx-slider--negative.nx-slider--disabled .nx-slider__filler{background-color:GrayText}}\n"], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: NxSliderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'nx-slider', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => NxSliderComponent),
                            multi: true,
                        },
                    ], host: {
                        '[attr.aria-disabled]': 'disabled ? true : null',
                        '(keydown)': 'handleKeypress($event)',
                        '[class.nx-slider--disabled]': 'disabled',
                        '[class.nx-slider--negative]': 'negative',
                    }, template: "<label class=\"nx-slider__label\" [id]=\"id + '-label'\" [attr.for]=\"id + '-handle'\" (click)=\"_focusHandleElement()\">{{ label }}</label>\n\n<div class=\"nx-slider__wrapper\" (click)=\"sliderClick($event)\">\n    <span class=\"nx-slider__background\">\n        <span class=\"nx-slider__filler\" [style.width.%]=\"percentageValue\"></span>\n\n        <div\n            class=\"nx-slider__handle\"\n            [id]=\"id + '-handle'\"\n            [attr.tabindex]=\"tabindex\"\n            [attr.aria-labelledby]=\"id + '-label'\"\n            role=\"slider\"\n            [attr.aria-valuemin]=\"formatLabelMin()\"\n            [attr.aria-valuemax]=\"formatLabelMax()\"\n            [attr.aria-valuetext]=\"formatValue(value)\"\n            [attr.aria-valuenow]=\"formatValue(value)\"\n            (mousedown)=\"dragStart($event)\"\n            (touchstart)=\"dragStart($event)\"\n            (selectstart)=\"selectStart()\"\n            #handle\n        >\n            <span *ngIf=\"thumbLabel\" class=\"nx-slider__value\"\n                >{{ formatValue(value) }}\n                <div class=\"nx-slider__arrow\"> </div>\n            </span>\n        </div>\n    </span>\n</div>\n\n<div *ngIf=\"!hideLabels\" class=\"nx-slider__label-container\">\n    <span class=\"nx-slider__value-label\">{{ formatLabelLeft() }}</span>\n    <span class=\"nx-slider__value-label\">{{ formatLabelRight() }}</span>\n</div>\n", styles: [":host{display:block}.nx-slider__label{font-size:var(--slider-label-font-size);line-height:var(--slider-label-line-height);font-weight:var(--slider-label-font-weight);letter-spacing:var(--slider-label-letter-spacing);color:var(--slider-label-color)}.nx-slider__wrapper{width:100%;padding:66px 0 18px}.nx-slider__background{position:relative;background-color:var(--slider-background-color);display:block;height:4px;border-radius:2px;white-space:nowrap;cursor:pointer}.nx-slider__filler{display:inline-block;height:4px;border-radius:2px;background-color:var(--slider-indicator-color);float:left}:host-context([dir=rtl]) .nx-slider__filler{float:right}.nx-slider__handle{display:inline-block;position:relative;width:24px;height:24px;border-radius:50%;box-shadow:var(--shadow-small);border-style:solid;border-width:1px;border-color:var(--slider-handle-border-color);background:var(--slider-handle-background-color);box-sizing:border-box;margin-left:-12px;top:-10px;z-index:2;cursor:-webkit-grab;cursor:grab}:host-context([dir=rtl]) .nx-slider__handle{margin-right:-12px;margin-left:0;margin-left:initial}.nx-slider__handle:focus{outline:none;border-color:var(--slider-handle-focus-border-color)}.nx-slider__handle:active{box-shadow:var(--shadow-small),inset 0 0 0 4px var(--slider-handle-background-color),inset 0 0 0 24px var(--slider-handle-active-inset-color);cursor:-webkit-grabbing;cursor:grabbing}.nx-slider__handle.cdk-keyboard-focused{border-color:var(--slider-handle-focus-border-color);box-shadow:var(--focus-box-shadow)}@media screen and (-ms-high-contrast: active){.nx-slider__handle.cdk-keyboard-focused{box-shadow:0 0 0 2px background,0 0 0 6px windowText;outline:4px solid CanvasText;outline-offset:2px}}.nx-slider__value{font-size:var(--slider-handle-value-font-size);line-height:var(--slider-handle-value-line-height);font-weight:var(--slider-handle-value-font-weight);letter-spacing:var(--slider-handle-value-letter-spacing);color:var(--slider-handle-value-color);border:1px solid;border-color:var(--slider-handle-border-color);border-radius:4px;box-shadow:var(--shadow-small);cursor:-webkit-grab;cursor:grab;height:24px;width:48px;text-align:center;right:8px;padding:3px 8px;position:relative;top:-40px;display:table;left:50%;transform:translate(-50%);background:var(--slider-value-background-color)}:host-context([dir=rtl]) .nx-slider__value{right:50%;left:8px;transform:translate(50%)}.nx-slider__value:active{cursor:-webkit-grabbing;cursor:grabbing}.nx-slider__arrow{width:6px;height:6px;border-right:1px solid;border-bottom:1px solid;left:50%;transform:translate(-50%,1px) rotate(45deg);position:absolute;box-shadow:-2px -2px 1px 1px var(--slider-value-background-color),2px 2px 4px var(--shadow-grey-color);background:var(--slider-value-background-color);border-color:var(--slider-handle-border-color)}.nx-slider__label-container{display:flex;width:100%;justify-content:space-between;font-size:var(--slider-value-label-font-size);line-height:var(--slider-value-label-line-height);font-weight:var(--slider-value-label-font-weight);letter-spacing:var(--slider-value-label-letter-spacing);color:var(--slider-value-label-color)}.nx-slider__max-value-label{text-align:right}:host(.nx-slider--disabled){cursor:not-allowed}:host(.nx-slider--disabled) .nx-slider__filler,:host(.nx-slider--disabled) .nx-slider__value,:host(.nx-slider--disabled) .nx-slider__value-label,:host(.nx-slider--disabled) .nx-slider__handle,:host(.nx-slider--disabled) .nx-slider__background{pointer-events:none;cursor:not-allowed}:host(.nx-slider--disabled) .nx-slider__value,:host(.nx-slider--disabled) .nx-slider__handle{background-color:var(--slider-value-disabled-background-color)}:host(.nx-slider--disabled) .nx-slider__filler{background-color:var(--slider-indicator-disabled-color)}:host(.nx-slider--disabled) .nx-slider__value{color:var(--slider-handle-value-disabled-color)}:host(.nx-slider--disabled) .nx-slider__label-container{color:var(--slider-value-label-disabled-color)}:host(.nx-slider--disabled) .nx-slider__arrow{background:var(--slider-value-disabled-background-color);box-shadow:-2px -2px 1px 1px var(--slider-value-disabled-background-color),2px 2px 4px #41414180}:host(.nx-slider--disabled) .nx-slider__background{background:var(--slider-disabled-background-color)}:host(.nx-slider--negative) .nx-slider__label,:host(.nx-slider--negative) .nx-slider__value-label{color:var(--negative)}:host(.nx-slider--negative) .nx-slider__filler{background-color:var(--negative)}:host(.nx-slider--negative) .nx-slider__background{background-color:var(--slider-negative-background-color)}:host(.nx-slider--negative.nx-slider--disabled) .nx-slider__label,:host(.nx-slider--negative.nx-slider--disabled) .nx-slider__value-label{color:var(--negative-01)}:host(.nx-slider--negative.nx-slider--disabled) .nx-slider__filler{background-color:var(--negative-01)}@media screen and (-ms-high-contrast: active){.nx-slider__background{background-color:buttonFace;border:1px solid buttonText}.nx-slider__filler{background-color:highlight}.nx-slider__handle{background-color:buttonFace;border-color:buttonText}.nx-slider__handle:hover,.nx-slider__handle:focus{background-color:highlight}.nx-slider__handle:active{background-color:buttonText}.nx-slider__value{border-color:buttonText;color:buttonText}:host(.nx-slider--disabled) .nx-slider__background{background-color:Canvas;border:1px solid GrayText}:host(.nx-slider--disabled) .nx-slider__filler{background-color:GrayText}:host(.nx-slider--disabled) .nx-slider__handle{background-color:GrayText}:host(.nx-slider--disabled) .nx-slider__value{border-color:GrayText;color:GrayText}:host(.nx-slider--disabled) .nx-slider__arrow{border-color:GrayText}:host.nx-slider--negative.nx-slider--disabled .nx-slider__filler{background-color:GrayText}}\n"] }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: i1.Directionality, decorators: [{
                        type: Optional
                    }] }, { type: i2.FocusMonitor }];
    }, propDecorators: { handleElement: [{
                type: ViewChild,
                args: ['handle', { static: true }]
            }], id: [{
                type: Input,
                args: ['id']
            }], tabindex: [{
                type: Input
            }], min: [{
                type: Input,
                args: ['nxMin']
            }], max: [{
                type: Input,
                args: ['nxMax']
            }], step: [{
                type: Input,
                args: ['nxStep']
            }], label: [{
                type: Input,
                args: ['nxLabel']
            }], disabled: [{
                type: Input
            }], inverted: [{
                type: Input,
                args: ['nxInverted']
            }], thumbLabel: [{
                type: Input
            }], negative: [{
                type: Input,
                args: ['negative']
            }], hideLabels: [{
                type: Input,
                args: ['hideLabels']
            }], valueChange: [{
                type: Output,
                args: ['nxValueChange']
            }], valueFormatter: [{
                type: Input,
                args: ['nxValueFormatter']
            }], labelMinFormatter: [{
                type: Input,
                args: ['nxLabelMinFormatter']
            }], labelMaxFormatter: [{
                type: Input,
                args: ['nxLabelMaxFormatter']
            }], value: [{
                type: Input,
                args: ['nxValue']
            }] } });

class NxSliderModule {
}
NxSliderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: NxSliderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NxSliderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: NxSliderModule, declarations: [NxSliderComponent], imports: [CommonModule, NxGridModule], exports: [NxSliderComponent] });
NxSliderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: NxSliderModule, imports: [[CommonModule, NxGridModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: NxSliderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, NxGridModule],
                    declarations: [NxSliderComponent],
                    exports: [NxSliderComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { NxSliderComponent, NxSliderModule };
